// src/new/components/monitor/TriageChat/vedaSession.ts

/** Create a veda-ai chat session (no MCP apps in this cut) and return its id. */
export async function createVedaSession(baseUrl: string, token: string): Promise<string> {
  const res = await fetch(`${baseUrl}/api/v1/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ mcp_ids: [] }),
  });
  if (!res.ok) {
    throw new Error(`veda-ai session create failed: ${res.status}`);
  }
  const data = (await res.json()) as { session_id: string };
  if (!data.session_id) {
    throw new Error('veda-ai session create returned no session_id');
  }
  return data.session_id;
}

export interface HistoryMessage {
  role: 'user' | 'assistant' | 'tool';
  text: string;
}

/** Fetch a session's prior messages so a reused/reopened session shows the full
 *  conversation (and the agent keeps context). Maps veda-ai SessionMessage
 *  content blocks → simple {role,text}. Returns [] on any failure (caller then
 *  treats the session as fresh and seeds it). */
export async function getVedaSessionMessages(
  baseUrl: string,
  token: string,
  sessionId: string,
): Promise<HistoryMessage[]> {
  const res = await fetch(`${baseUrl}/api/v1/sessions/${sessionId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`veda-ai get session failed: ${res.status}`);
  const data = (await res.json()) as { session_messages?: any[] };
  const msgs = Array.isArray(data?.session_messages) ? data.session_messages : [];
  const out: HistoryMessage[] = [];
  for (const m of msgs) {
    const role = String(m?.role ?? '').toLowerCase();
    const blocks = Array.isArray(m?.content) ? m.content : [];
    if (role === 'user') {
      const text = blocks
        .filter((b: any) => b?.type === 'user_text_prompt' || b?.type === 'text')
        .map((b: any) => b?.text ?? '')
        .join('');
      if (text.trim()) out.push({ role: 'user', text });
    } else if (role === 'agent' || role === 'assistant') {
      for (const b of blocks) {
        if ((b?.type === 'agent_text_response' || b?.type === 'text') && (b?.text ?? '').trim()) {
          out.push({ role: 'assistant', text: b.text });
        } else if (b?.type === 'tool_request') {
          out.push({ role: 'tool', text: b?.tool_display_name || b?.tool_name || 'Tool call' });
        }
      }
    }
  }
  return out;
}

/** Build the WebSocket stream URL for a session from the http(s) base url.
 *  veda-ai mounts its routers under `/api/v1` (app.include_router(prefix="/api/v1")),
 *  so the WS route is `/api/v1/sessions/{id}/stream` — matching veda-ui's
 *  getSocketUrl(`${WS_BASE_URL}/v1/sessions/{id}/stream`). An unmatched WS path
 *  returns Starlette 403, which is what the missing prefix caused. */
export function vedaWebSocketUrl(baseUrl: string, sessionId: string): string {
  const wsBase = baseUrl.replace(/^http/, 'ws');
  return `${wsBase}/api/v1/sessions/${sessionId}/stream`;
}
